import {
  Card,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
} from '@material-ui/core';
import { Button } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Subscription } from 'rxjs';

import { SignAndStampUserConsentFileDocument } from '../../rxdb/signAndStampUserConsent/queryBuilder';
import { useSignAndStampUserConsent } from '../../rxdb/signAndStampUserConsent/useSignAndStampUserConsent';

const useStyles = makeStyles(() =>
  createStyles({
    grid: {
      paddingBottom: '14px',
      paddingLeft: '16px',
      paddingTop: '16px',
    },
    card: {
      backgroundColor: 'rgba(255,255,255,0.05)',
      paddingTop: '12px',
      paddingLeft: '8px',
      paddingRight: '8px',
    },
    textGrid: {
      paddingLeft: '15px',
    },
    termsGrid: {
      paddingTop: '16px',
      paddingLeft: '15px',
      paddingBottom: '15px',
    },
    termsList: {
      paddingLeft: '15px',
    },
    saveGrid: {
      justifyContent: 'flex-end',
      display: 'grid',
      '& .saveButton': {
        width: '100px',
        fontWeight: 700,
      },
    },
    dialogActionGrid: {
      height: '70px',
      overflow: 'hidden',
      background: 'rgba(255,255,255,0.09)',
      '& .cancelButton': {
        width: '100px',
      },
    },
    title: {
      textAlignLast: 'center',
      background: 'rgba(255,255,255,0.09)',
    },
    dialogContent: {
      paddingLeft: '8px',
      paddingRight: '8px',
      paddingBottom: '20px',
      marginTop: '5px',
      background: '#272727',
      marginBottom: '5px',
    },
    data: {
      fontSize: '16px',
      fontFamily: 'open sans',
      whiteSpace: 'inherit',
      textAlign: 'justify',
      padding: '0px 12px',
      '& b': {
        fontSize: '30px',
        fontWeight: 300,
        fontFamily: 'open sans',
      },
      '& p': {
        marginBlockStart: 20,
        letterSpacing: '0.5px',
        fontFamily: 'open sans',
        lineHeight: '24px',
      },
      '& ol': {
        fontFamily: 'open sans',
        paddingInlineStart: '25px',
        letterSpacing: '0.5px',
        lineHeight: '24px',
      },
      '& li': {
        letterSpacing: '0.5px',
        lineHeight: '24px',
        fontWeight: 400,
        paddingTop: '10px',
      },
      '& div': {
        display: 'inline-flex',
      },
      '& span': {
        paddingTop: '10px',
        paddingRight: '10px',
      },
    },
  }),
);

interface Props {
  open: boolean;
  onClose: () => void;
  setIsAccepted: (val: boolean) => void;
  setIsChecked: (val: boolean) => void;
  setIsOpenAlert: (val: boolean) => void;
}

const UserConsent: React.FC<Props> = ({
  open,
  onClose,
  setIsAccepted,
  setIsChecked,
  setIsOpenAlert,
}) => {
  const classes = useStyles();
  const [UserContentAvailable, setUserContentAvailable] = useState<boolean>(false);
  const [signAndStampUserConsentDataDocument, setSignAndStampUserConsentDataDocument] =
    useState<string>('');
  const [signAndStampUserConsentUpdateSubscription, setSignAndStampUserConsentUpdateSubscription] =
    useState<Subscription>();
  const handleClick = () => {
    onClose();
    setIsAccepted(true);
    setIsChecked(true);
    setIsOpenAlert(false);
  };
  const {
    signAndStampUserConsentInitialized,
    signAndStampUserConsentFileCollection,
    getSignAndStampUserConsent,
  } = useSignAndStampUserConsent();

  useEffect(() => {
    if (signAndStampUserConsentDataDocument) {
      if (signAndStampUserConsentDataDocument.length > 0) setUserContentAvailable(true);
      else setUserContentAvailable(false);
    }
  }, [signAndStampUserConsentDataDocument]);

  const handleKeyEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleClick();
    }
  };

  const getSignAndStampUserConsentFromRxDB = async () => {
    if (signAndStampUserConsentFileCollection) {
      const signAndStampUserConsentFileRxDocument: SignAndStampUserConsentFileDocument[] =
        await getSignAndStampUserConsent();
      if (signAndStampUserConsentFileRxDocument) {
        signAndStampUserConsentFileRxDocument.map(async item => {
          setSignAndStampUserConsentDataDocument(item.userConsentData);
        });
      }
    }
  };
  const subscribeToSignAndStampUserConsent = async () => {
    if (signAndStampUserConsentInitialized && signAndStampUserConsentFileCollection) {
      const signAndStampUserConsentUpdateSub = signAndStampUserConsentFileCollection
        .find()
        .$.subscribe(() => {
          getSignAndStampUserConsentFromRxDB();
        });
      setSignAndStampUserConsentUpdateSubscription(signAndStampUserConsentUpdateSub);
    }
  };
  useEffect(() => {
    if (signAndStampUserConsentInitialized) {
      getSignAndStampUserConsentFromRxDB();
      subscribeToSignAndStampUserConsent();
    }
    return () => {
      if (signAndStampUserConsentUpdateSubscription)
        signAndStampUserConsentUpdateSubscription.unsubscribe();
    };
  }, [signAndStampUserConsentInitialized]);

  return (
    <>
      <Dialog onKeyUp={handleKeyEvent} open={open} onClose={onClose} fullScreen disableEnforceFocus>
        <DialogTitle className={classes.title}>User Consent</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {UserContentAvailable && (
            <Card className={classes.card}>
              <pre className={classes.data}>
                <span dangerouslySetInnerHTML={{ __html: signAndStampUserConsentDataDocument }} />
              </pre>
            </Card>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActionGrid}>
          <Grid container xs={12}>
            <Grid item xs={2}>
              <Button
                variant="outlined"
                color="primary"
                className="cancelButton"
                onClick={onClose}
                data-testid="cancel-user-consent"
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={2} container justifyContent="flex-end" className={classes.saveGrid}>
              <Button
                variant="contained"
                color="primary"
                className="saveButton"
                onClick={handleClick}
                data-testid="accept-user-consent"
              >
                Accept
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(UserConsent);
